import React from 'react';
import { Link } from 'react-router-dom';

export type Props = Readonly<{
  to: string;
  smooth?: boolean;
  title?: string;
  onClick?: () => void;
  children: React.ReactNode;
}>;

/**
 * Minimal drop-in replacement for react-router-hash-link's `HashLink` at its one current call
 * site, built on react-router-dom's `Link`. Exposes only the props that call site needs — it is
 * not a full `HashLink`/`Link` prop surface.
 *
 * On an unmodified left click, synchronously scrolls the element matching the hash portion of
 * `to` into view (smoothly unless `smooth={false}`) in addition to the normal `Link` navigation.
 * This assumes the target element already exists in the DOM at click time (true for same-page
 * hash targets); it does not wait for a route transition to mount a new target. Matches
 * react-router's own click-processing rules by skipping the scroll on modified clicks
 * (cmd/ctrl/shift/alt-click) or non-primary buttons (e.g. middle-click), since those open the
 * link in a new tab/window instead of navigating the current page.
 */
export const HashScrollLink: React.FC<Props> = ({
  to,
  smooth = true,
  title,
  onClick,
  children,
}) => {
  const handleClick = (event: React.MouseEvent<HTMLAnchorElement>) => {
    const isModifiedClick =
      event.button !== 0 || event.metaKey || event.altKey || event.ctrlKey || event.shiftKey;
    if (isModifiedClick) {
      return;
    }

    onClick?.();

    const id = to.split('#')[1];
    if (id) {
      document.getElementById(id)?.scrollIntoView({ behavior: smooth ? 'smooth' : 'auto' });
    }
  };

  return (
    <Link to={to} title={title} onClick={handleClick}>
      {children}
    </Link>
  );
};
